import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Pencil, Boxes, ArrowRightLeft, Trash2, MoreVertical, Search } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Input, Label, Card, CardContent, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../ui'
import {
  getInventoryOverview, deleteInventoryItem, registrarMovimiento,
} from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import ItemFormModal from './ItemFormModal'
import { useDeportes } from '../../../hooks/useDeportes'

const CATEGORIA_LABELS = {
  cono: 'Cono',
  balon: 'Balón',
  dummy: 'Dummy',
  casco: 'Casco',
  hombrera: 'Hombrera',
  proteccion: 'Protección',
  otro: 'Otro',
}

const MOVIMIENTO_OPTIONS = [
  { value: 'alta', label: 'Alta (entrada)' },
  { value: 'perdida', label: 'Pérdida' },
  { value: 'desecho', label: 'Desecho' },
]

const initialMovForm = { tipo: 'alta', cantidad: '', motivo: '' }

const InventarioArticulos = () => {
  const { label: deporteLabel } = useDeportes()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const [formOpen, setFormOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)

  const movGuard = useUnsavedGuard({ onClose: () => setMovOpen(false) })
  const [movOpen, setMovOpen] = useState(false)
  const [movItem, setMovItem] = useState(null)
  const [movForm, setMovForm] = useState(initialMovForm)
  const [savingMov, setSavingMov] = useState(false)
  const [movError, setMovError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await getInventoryOverview()
    setItems(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditingItem(null)
    setFormOpen(true)
  }

  const openEdit = (item) => {
    setEditingItem(item)
    setFormOpen(true)
  }

  const openMovimiento = (item) => {
    setMovItem(item)
    setMovForm(initialMovForm)
    setMovError(null)
    movGuard.resetDirty()
    setMovOpen(true)
  }

  const handleSaveMovimiento = async (e) => {
    e.preventDefault()
    setMovError(null)
    const cantidad = Number(movForm.cantidad)
    if (!cantidad || cantidad <= 0) {
      setMovError('Captura una cantidad mayor a cero')
      return
    }
    setSavingMov(true)
    const { error } = await registrarMovimiento({
      itemId: movItem.id,
      tipo: movForm.tipo,
      cantidad,
      motivo: movForm.motivo.trim() || null,
    })
    setSavingMov(false)
    if (error) {
      setMovError(error.message || 'No se pudo registrar el movimiento')
      return
    }
    movGuard.resetDirty()
    setMovOpen(false)
    load()
  }

  const handleDelete = async (item) => {
    const ok = await confirm(
      'Dar de baja artículo',
      `¿Dar de baja "${item.nombre}"? Dejará de aparecer en el inventario, pero su historial se conserva.`,
      { destructive: true, confirmText: 'Dar de baja' }
    )
    if (!ok) return
    const { error } = await deleteInventoryItem(item.id)
    if (error) {
      await alert('Error', error.message || 'No se pudo dar de baja el artículo')
      return
    }
    load()
  }

  const term = search.trim().toLowerCase()
  const itemsFiltrados = term
    ? items.filter((i) =>
        [i.nombre, i.categoria, i.talla, i.modelo, i.deporte]
          .some((v) => (v || '').toString().toLowerCase().includes(term))
      )
    : items

  return (
    <FeaturedLayout
      title="Inventario"
      subtitle="Artículos y existencias del club"
      refreshAction={load}
    >
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar artículo..."
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
          <span className="font-medium text-foreground">{itemsFiltrados.length}</span>{' '}
          {itemsFiltrados.length === 1 ? 'artículo' : 'artículos'}
        </span>
        <Button onClick={openCreate} className="h-10 shrink-0" aria-label="Nuevo artículo" title="Nuevo artículo">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuevo artículo</span>
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Boxes className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">No hay artículos registrados</p>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" /> Crear primer artículo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artículo</TableHead>
                <TableHead className="hidden sm:table-cell">Categoría</TableHead>
                <TableHead className="hidden md:table-cell">Deporte</TableHead>
                <TableHead className="hidden lg:table-cell">Ubicación</TableHead>
                <TableHead className="text-right">Existencias</TableHead>
                <TableHead className="w-28 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itemsFiltrados.map((item) => {
                const detalle = [item.talla, item.modelo].filter(Boolean).join(' · ')
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{item.nombre}</span>
                        {detalle && <p className="text-xs text-muted-foreground">{detalle}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {item.categoria ? (
                        <Badge variant="secondary" className="text-xs">
                          {CATEGORIA_LABELS[item.categoria] || item.categoria}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm">
                      {item.deporte_id ? deporteLabel(item.deporte_id) : 'General'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {item.ubicacion || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 justify-end">
                        <Badge variant="outline" className="text-[10px]">Total {item.total}</Badge>
                        <Badge variant="outline" className="text-[10px]">Prestados {item.prestados}</Badge>
                        <Badge
                          variant={item.disponibles < 0 ? 'destructive' : 'default'}
                          className="text-[10px]"
                        >
                          Disponibles {item.disponibles}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => openEdit(item)}>
                          <Pencil className="h-3.5 w-3.5 sm:mr-1" />
                          <span className="hidden sm:inline">Editar</span>
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Más acciones">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openMovimiento(item)}>
                              <ArrowRightLeft className="h-4 w-4 mr-2" />
                              Registrar movimiento
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(item)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Dar de baja
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <ItemFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        item={editingItem}
        onSuccess={load}
      />

      {/* Registrar movimiento */}
      <Dialog open={movOpen} onOpenChange={movGuard.onOpenChange}>
        <DialogContent {...movGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Registrar movimiento{movItem ? ` · ${movItem.nombre}` : ''}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveMovimiento} className="space-y-4">
            {movError && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                {movError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="mov-tipo">Tipo de movimiento</Label>
              <Select value={movForm.tipo} onValueChange={(v) => { movGuard.markDirty(); setMovForm((p) => ({ ...p, tipo: v })) }}>
                <SelectTrigger id="mov-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOVIMIENTO_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mov-cantidad">Cantidad</Label>
              <Input
                id="mov-cantidad"
                type="number"
                min="1"
                value={movForm.cantidad}
                onChange={(e) => setMovForm((p) => ({ ...p, cantidad: e.target.value }))}
                placeholder="0"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mov-motivo">Motivo</Label>
              <Input
                id="mov-motivo"
                value={movForm.motivo}
                onChange={(e) => setMovForm((p) => ({ ...p, motivo: e.target.value }))}
                placeholder="Opcional"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => movGuard.onOpenChange(false)} disabled={savingMov}>
                Cancelar
              </Button>
              <Button type="submit" disabled={savingMov}>
                {savingMov ? 'Guardando...' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
          {movGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default InventarioArticulos
